import React, { useRef, useState } from "react";
import { dateISOtoStringShortUTC } from "./momentUtils";
import { ButtonOrange, TableHeader2 } from "./formComponents";
import COLORS from "./styleConstants";
import Auth from "./Auth";

const fetchDueSubscriptionCharges = async () => {
  const result = await Auth.api(
    "subscriptions/getDueSubscriptionCharges",
    "get",
  );
  const data = result?.data;
  const charges = Array.isArray(data) ? data : data?.subscription_charges;
  if (!Array.isArray(charges)) {
    throw new Error("Unable to confirm the due subscription charge list.");
  }
  return charges;
};

export const AdminSubscriptionDueCharges = () => {
  const [loadingDueCharges, setLoadingDueCharges] = useState(false);
  const [chargingDueSubscriptions, setChargingDueSubscriptions] =
    useState(false);
  const [dueChargesNotice, setDueChargesNotice] = useState("");
  const [dueSubscriptionCharges, setDueSubscriptionCharges] = useState([]);
  const [hasCheckedDueCharges, setHasCheckedDueCharges] = useState(false);
  const chargingDueRef = useRef(false);

  const loadDueSubscriptionCharges = async () => {
    setLoadingDueCharges(true);
    setDueChargesNotice("");
    try {
      const chargeList = await fetchDueSubscriptionCharges();
      setDueSubscriptionCharges(chargeList);
      setHasCheckedDueCharges(true);
      setDueChargesNotice(
        `${chargeList.length} subscription charge${chargeList.length === 1 ? "" : "s"} due.`,
      );
    } catch (error) {
      setDueChargesNotice(
        error.response?.data?.message ||
          error.message ||
          "Unable to load due subscription charges.",
      );
    } finally {
      setLoadingDueCharges(false);
    }
  };

  const chargeDueSubscriptions = async () => {
    if (
      chargingDueRef.current ||
      !window.confirm("Charge all subscriptions that are currently due?")
    )
      return;

    chargingDueRef.current = true;
    setChargingDueSubscriptions(true);
    setDueChargesNotice("");
    try {
      const result = await Auth.api(
        "subscriptions/chargeDueSubscriptions",
        "post",
        {},
      );
      if (
        !result ||
        result.status < 200 ||
        result.status >= 300 ||
        result.data?.success === false ||
        result.data?.error
      ) {
        throw new Error(
          result?.data?.message ||
            result?.data?.error ||
            "Charging due subscriptions was not confirmed.",
        );
      }

      setDueChargesNotice(
        "Subscription charges have been queued for processing. Payment results are not yet confirmed.",
      );
    } catch (error) {
      setDueChargesNotice(
        error.response?.data?.message ||
          error.message ||
          "Unable to charge due subscriptions.",
      );
    } finally {
      chargingDueRef.current = false;
      setChargingDueSubscriptions(false);
    }
  };

  return (
    <section style={{ width: "100%", minWidth: 0, padding: 20 }}>
      <div style={{ marginBottom: 16 }}>
        <TableHeader2 title="Due Subscription Charges" />
        <p style={{ color: COLORS.textgrey, margin: "8px 0 0" }}>
          Review scheduled charges before queuing the payment job. Results
          appear in Payment Failures after processing completes.
        </p>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        <ButtonOrange
          type="button"
          disabled={loadingDueCharges || chargingDueSubscriptions}
          onClick={loadDueSubscriptionCharges}
          label={loadingDueCharges ? "Checking..." : "Check Due Charges"}
        />
        <ButtonOrange
          type="button"
          disabled={loadingDueCharges || chargingDueSubscriptions}
          onClick={chargeDueSubscriptions}
          label={
            chargingDueSubscriptions
              ? "Queueing..."
              : "Charge Due Subscriptions"
          }
        />
      </div>
      {dueChargesNotice && (
        <div role="status" style={{ marginTop: 12, marginBottom: 12 }}>
          {dueChargesNotice}
        </div>
      )}
      {hasCheckedDueCharges && dueSubscriptionCharges.length === 0 && (
        <div style={{ marginTop: 12 }}>
          No subscription charges are currently due.
        </div>
      )}
      {dueSubscriptionCharges.length > 0 && (
        <div style={{ maxWidth: "100%", overflowX: "auto", marginTop: 16 }}>
          <table
            style={{ width: "100%", borderCollapse: "collapse", minWidth: 520 }}
          >
            <thead>
              <tr style={{ color: COLORS.blue, textAlign: "left" }}>
                <th style={{ padding: "6px 8px" }}>Scheduled charge date</th>
                <th style={{ padding: "6px 8px" }}>Billing person</th>
                <th style={{ padding: "6px 8px", textAlign: "right" }}>
                  Total
                </th>
              </tr>
            </thead>
            <tbody>
              {dueSubscriptionCharges.map((charge) => {
                let total;
                try {
                  total = new Intl.NumberFormat(undefined, {
                    style: "currency",
                    currency: charge.currency?.toUpperCase() || "USD",
                  }).format(Number(charge.total));
                } catch {
                  total = `${charge.total} ${charge.currency || ""}`.trim();
                }

                return (
                  <tr
                    key={charge.id}
                    style={{ borderTop: "1px solid #e2e5e9" }}
                  >
                    <td style={{ padding: "7px 8px" }}>
                      {dateISOtoStringShortUTC(charge.scheduled_charge_at)}
                    </td>
                    <td style={{ padding: "7px 8px" }}>
                      {charge.billing_person_name || "-"}
                    </td>
                    <td style={{ padding: "7px 8px", textAlign: "right" }}>
                      {total}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};
